import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';

export default function RoleInlineEdit({
  value,
  placeholder = 'Add title',
  onSave,
  className,
  inputAriaLabel = 'Title',
  disabled = false,
}) {
  const initial = useMemo(() => (value ?? '').toString(), [value]);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(initial);
    }
  }, [initial, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    const t = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }, 0);
    return () => clearTimeout(t);
  }, [isEditing]);

  useEffect(() => {
    if (!justSaved) return;
    const t = setTimeout(() => setJustSaved(false), 1300);
    return () => clearTimeout(t);
  }, [justSaved]);

  const start = () => {
    if (disabled || isSaving) return;
    setIsEditing(true);
  };

  const cancel = () => {
    if (isSaving) return;
    setDraft(initial);
    setIsEditing(false);
  };

  const save = async () => {
    if (disabled || isSaving) return;
    const next = draft.trim();
    if (next === initial.trim()) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(next);
      setIsEditing(false);
      setJustSaved(true);
    } finally {
      setIsSaving(false);
    }
  };

  const showText = (initial || '').trim() || placeholder;

  return (
    <div
      className={[
        'role-editable-wrap',
        isEditing ? 'editing' : '',
        justSaved ? 'just-saved' : '',
        disabled ? 'disabled' : '',
        className || '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={start}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (disabled) return;
        if ((e.key === 'Enter' || e.key === ' ') && !isEditing) {
          e.preventDefault();
          start();
        }
      }}
      aria-label="Edit title"
    >
      <span
        className={['role-text', !initial?.trim() ? 'placeholder' : ''].join(
          ' '
        )}
      >
        {showText}
      </span>
      <Pencil
        style={{ width: '13px', height: '13px' }}
        className="role-edit-icon"
      />

      <div className="role-input-group" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="role-input"
          type="text"
          value={draft}
          aria-label={inputAriaLabel}
          disabled={disabled || isSaving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              save();
            } else if (e.key === 'Escape') {
              e.preventDefault();
              cancel();
            }
          }}
        />
        <div className="role-input-actions">
          <button
            type="button"
            className="role-save-btn"
            onClick={save}
            title="Save"
            aria-label="Save title"
            disabled={disabled || isSaving}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </button>
          <button
            type="button"
            className="role-cancel-btn"
            onClick={cancel}
            title="Cancel"
            aria-label="Cancel editing title"
            disabled={disabled || isSaving}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
